import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// Extrai o ID da planilha de uma URL do Google Sheets
export function extractSheetId(url: string): string | null {
  const patterns = [
    /\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/,
    /^([a-zA-Z0-9-_]+)$/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }

  return null;
}

// Interface para os dados do Google Sheets
export interface SheetData {
  headers: string[];
  rows: any[];
}

/**
 * Busca dados de uma planilha do Google Sheets
 * NOTA: Esta operação usa a API Key configurada no SERVIDOR (backend)
 * Clientes não precisam configurar API Key individual
 * 
 * @param spreadsheetId - ID da planilha ou URL completa
 * @param range - Range a ser lido (ex: "Sheet1!A1:Z1000" ou apenas "Sheet1")
 */
export async function fetchSheetData(
  spreadsheetId: string,
  range: string = 'Sheet1'
): Promise<SheetData> {
  try {
    const response = await axios.get(`${API_URL}/google-sheets/data`, {
      params: {
        spreadsheetId,
        range,
      },
    });

    return response.data;
  } catch (error: any) {
    console.error('Erro ao buscar dados do Google Sheets:', error);
    
    const errorMessage = error.response?.data?.message || error.message;
    throw new Error(errorMessage);
  }
}

/**
 * Busca as abas (sheets) disponíveis em uma planilha
 * @param spreadsheetId - ID da planilha ou URL completa
 * @returns Array com os nomes das abas
 */
export async function fetchSheetNames(
  spreadsheetId: string
): Promise<string[]> {
  try {
    const response = await axios.get(`${API_URL}/google-sheets/sheets`, {
      params: {
        spreadsheetId,
      },
    });

    return response.data.sheets || [];
  } catch (error: any) {
    console.error('Erro ao buscar abas do Google Sheets:', error);
    const errorMessage = error.response?.data?.message || error.message;
    throw new Error(errorMessage);
  }
}

/**
 * Atualiza dados em uma planilha do Google Sheets
 * Usa a Service Account configurada no servidor
 */
export async function updateSheetData(
  spreadsheetId: string,
  range: string,
  data: any[][]
): Promise<void> {
  try {
    await axios.put(
      `${API_URL}/google-sheets/data`,
      {
        spreadsheetId,
        range,
        data,
      }
    );
  } catch (error: any) {
    console.error('Erro ao atualizar Google Sheets:', error);
    const errorMessage = error.response?.data?.message || error.message;
    throw new Error(errorMessage);
  }
}

/**
 * Adiciona uma nova linha à planilha
 * Usa a Service Account configurada no servidor
 */
export async function appendSheetRow(
  spreadsheetId: string,
  range: string,
  row: any[]
): Promise<void> {
  try {
    await axios.post(
      `${API_URL}/google-sheets/row`,
      {
        spreadsheetId,
        range,
        row,
      }
    );
  } catch (error: any) {
    console.error('Erro ao adicionar linha ao Google Sheets:', error);
    const errorMessage = error.response?.data?.message || error.message;
    throw new Error(errorMessage);
  }
}

/**
 * Deleta uma linha específica da planilha
 * Usa a Service Account configurada no servidor
 * @param spreadsheetId - ID da planilha
 * @param sheetName - Nome da aba (ex: "Sheet1")
 * @param rowIndex - Índice da linha a deletar (0-based, considerando cabeçalho na linha 0)
 */
export async function deleteSheetRow(
  spreadsheetId: string,
  sheetName: string,
  rowIndex: number
): Promise<void> {
  try {
    await axios.delete(`${API_URL}/google-sheets/row`, {
      data: {
        spreadsheetId,
        sheetName,
        rowIndex,
      },
    });
  } catch (error: any) {
    console.error('Erro ao deletar linha do Google Sheets:', error);
    const errorMessage = error.response?.data?.message || error.message;
    throw new Error(errorMessage);
  }
}

/**
 * Atualiza uma linha específica da planilha
 * @param spreadsheetId - ID da planilha
 * @param range - Range base (ex: "Sheet1")
 * @param rowIndex - Índice da linha a atualizar (0-based, considerando cabeçalho na linha 0)
 * @param rowData - Dados da linha como objeto
 * @param headers - Cabeçalhos da planilha
 */
export async function updateSheetRow(
  spreadsheetId: string,
  range: string,
  rowIndex: number,
  rowData: any,
  headers: string[]
): Promise<void> {
  const sheetId = extractSheetId(spreadsheetId);
  
  if (!sheetId) {
    throw new Error('ID da planilha inválido');
  }

  // Converter objeto em array seguindo a ordem dos cabeçalhos
  const rowArray = headers.map(header => {
    const field = header.toLowerCase().replace(/\s+/g, '_');
    return rowData[field] !== undefined ? rowData[field] : '';
  });

  // Construir range específico para a linha (rowIndex + 2 porque: +1 para cabeçalho, +1 porque Sheets é 1-based)
  const rowRange = `${range}!A${rowIndex + 2}:${String.fromCharCode(65 + headers.length - 1)}${rowIndex + 2}`;
  
  await updateSheetData(spreadsheetId, rowRange, [rowArray]);
}

/**
 * Configuração de acesso para Google Sheets
 * 
 * MULTI-USUÁRIO:
 * - Cada usuário armazena seu próprio token no localStorage do navegador
 * - Tokens são pessoais e não compartilhados entre usuários
 * - Cada usuário precisa fazer sua própria autenticação OAuth2
 * - Tokens expiram após ~1 hora (padrão do Google)
 * - Operações são executadas em nome do usuário autenticado
 */
export const googleSheetsAuth = {
  setAccessToken(token: string) {
    localStorage.setItem('google_sheets_access_token', token);
  },
  
  getAccessToken(): string | null {
    return localStorage.getItem('google_sheets_access_token');
  },
  
  clearAccessToken() {
    localStorage.removeItem('google_sheets_access_token');
  },
  
  hasAccessToken(): boolean {
    return !!this.getAccessToken();
  },
};

export default {
  fetchSheetData,
  fetchSheetNames,
  updateSheetData,
  updateSheetRow,
  appendSheetRow,
  deleteSheetRow,
  extractSheetId,
  auth: googleSheetsAuth,
};
